import type { Store, StoreActions } from 'pinia';
import { watch } from 'vue';
import useTranslation from '../../composables/use-translation';
import { useGlobalToast } from './../../../../components/src/core/messages/toast';
import { useStoreAPIAction } from './../../../../stores/plugins/api';

/**
 * Monitors the readiness and errors of a specified API action in a Pinia store, displaying
 * toast notifications for error cases. This composable can be used to observe the completion
 * and potential errors of an asynchronous store action, and automatically trigger appropriate
 * user notifications.
 *
 * @template S - The store type, extending the Pinia `Store`.
 * @template AC - The action name, corresponding to a key in the store's actions.
 * @param {S} store - The store instance containing the action to be monitored.
 * @param {AC} actionName - The name of the action within the store to watch.
 *
 * @returns {{}} An empty object, as this composable is used primarily for its side effects.
 *
 * @example
 * const store = useSomeStore();
 * monitorAPIAction(store, 'someAction');
 * // This will watch the `someAction` in `useSomeStore` and show error messages as necessary.
 */

// prevents multiple toasts for a single error when multiple components on the same page compose the same error object
const toastedErrors = new WeakSet<object>();

export default <S extends Store, AC extends keyof StoreActions<S>>(store: S, actionName: AC) => {
  // Access `errors` and `ready` status from the store's API action.
  const { errors, ready } = useStoreAPIAction(store, actionName);

  // Access global toast notification
  const { showErrorToast } = useGlobalToast();

  // Access translation functionality
  const { translate } = useTranslation();

  /**
   * Watches `ready` and `errors` for changes to handle error notifications.
   * When `ready` is true and there are errors, it triggers a toast notification
   * based on the error status:
   * - Shows specific error messages for 400–499 errors.
   * - Shows a generic error message for other status codes.
   */
  watch([ready, errors], ([isReady, newErrors]) => {
    if (isReady && newErrors) {
      if (toastedErrors.has(newErrors)) return;
      toastedErrors.add(newErrors);

      if (newErrors.status) {
        if (newErrors.status >= 400 && newErrors.status < 500) {
          showErrorToast(newErrors?.error ? newErrors?.error : (newErrors?.message as string));
        } else {
          showErrorToast(translate('errorMessages.genericApiError'));
        }
      }
    }
  });

  return {};
};
